/**
 * Performance Monitoring Utilities
 */

export interface PerformanceMetric {
  name: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  tags?: Record<string, any>;
}

export class PerformanceMonitor {
  private metrics: Map<string, PerformanceMetric> = new Map();
  private observers: PerformanceObserverCallback[] = [];

  /**
   * Start measuring a metric
   */
  startMeasure(name: string, tags?: Record<string, any>): void {
    this.metrics.set(name, {
      name,
      startTime: performance.now(),
      tags,
    });
  }

  /**
   * End measuring a metric
   */
  endMeasure(name: string): PerformanceMetric | null {
    const metric = this.metrics.get(name);
    if (!metric) return null;

    metric.endTime = performance.now();
    metric.duration = metric.endTime - metric.startTime;

    this.notifyObservers(metric);

    if (metric.duration > 1000) {
      console.warn(`⚠️ Slow metric: ${name} took ${metric.duration.toFixed(2)}ms`, metric.tags);
    }

    return metric;
  }

  /**
   * Get metric
   */
  getMetric(name: string): PerformanceMetric | undefined {
    return this.metrics.get(name);
  }

  /**
   * Get all metrics
   */
  getAllMetrics(): PerformanceMetric[] {
    return Array.from(this.metrics.values());
  }

  /**
   * Clear metrics
   */
  clearMetrics(): void {
    this.metrics.clear();
  }

  /**
   * Subscribe to metric changes
   */
  subscribe(callback: PerformanceObserverCallback): () => void {
    this.observers.push(callback);
    return () => {
      this.observers = this.observers.filter((obs) => obs !== callback);
    };
  }

  private notifyObservers(metric: PerformanceMetric): void {
    this.observers.forEach((callback) => callback(metric));
  }

  /**
   * Generate performance report
   */
  getReport(): string {
    const metrics = Array.from(this.metrics.values());
    const sorted = metrics.sort((a, b) => (b.duration || 0) - (a.duration || 0));

    let report = '📊 Performance Report:\n';
    report += '========================\n';

    sorted.forEach((m) => {
      if (m.duration) {
        report += `${m.name}: ${m.duration.toFixed(2)}ms\n`;
      }
    });

    const totalDuration = sorted.reduce((sum, m) => sum + (m.duration || 0), 0);
    report += `\nTotal: ${totalDuration.toFixed(2)}ms`;

    return report;
  }
}

// Web Vitals monitoring
export interface WebVitals {
  LCP?: number; // Largest Contentful Paint
  FID?: number; // First Input Delay
  CLS?: number; // Cumulative Layout Shift
  TTFB?: number; // Time to First Byte
  FCP?: number; // First Contentful Paint
}

export function measureWebVitals(callback: (vitals: WebVitals) => void): void {
  const vitals: WebVitals = {};

  // LCP
  const lcpObserver = new PerformanceObserver((list) => {
    const entries = list.getEntries();
    const lastEntry = entries[entries.length - 1];
    const lcp: any = lastEntry as any;
    vitals.LCP = lcp.renderTime || lcp.loadTime || lcp.startTime;
  });

  try {
    lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    console.debug('LCP not supported');
  }

  // CLS
  let clsValue = 0;
  const clsObserver = new PerformanceObserver((list) => {
    list.getEntries().forEach((entry: any) => {
      if (!entry.hadRecentInput) {
        clsValue += entry.value;
        vitals.CLS = clsValue;
      }
    });
  });

  try {
    clsObserver.observe({ type: 'layout-shift', buffered: true });
  } catch (e) {
    console.debug('CLS not supported');
  }

  // Navigation Timing
  if (window.performance && window.performance.timing) {
    const timing = window.performance.timing;
    vitals.TTFB = timing.responseStart - timing.navigationStart;
    vitals.FCP = timing.domContentLoadedEventEnd - timing.navigationStart;
  }

  // Send after page load
  if (document.readyState === 'complete') {
    callback(vitals);
  } else {
    window.addEventListener('load', () => callback(vitals));
  }
}

type PerformanceObserverCallback = (metric: PerformanceMetric) => void;

// Global instance
export const performanceMonitor = new PerformanceMonitor();

/**
 * Hook for performance monitoring
 */
export function usePerfMeasure(name: string, tags?: Record<string, any>) {
  React.useEffect(() => {
    performanceMonitor.startMeasure(name, tags);

    return () => {
      performanceMonitor.endMeasure(name);
    };
  }, [name, tags]);
}

import React from 'react';
